import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, BehaviorSubject , throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { UserService } from './user';
import { IUser } from '@signature-it/ngx-generic/interfaces';
import { SignedUserDTO, UserFactory } from '@signature-it/ngx-generic/models';
import { ShareDataService } from './share-data.service';
import { PageNotificationService } from './page-notification';
import { RestUrlsConfig, GenericConfig } from '@signature-it/ngx-generic/config';
import {PhoneOrderSharedService} from './phone-order.shared';
declare let _: any;

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    protected routeAuthUrl = `${this.restUrlsConf.appURL}/public/auth/is_route_authenticated.json`;
    protected isCaptchaHidden = `${this.restUrlsConf.appURL}/public/auth/check_is_captcha_hidden.json?form=`;
    protected captchaHiddenContactUs = `${this.restUrlsConf.appURL}/public/auth/check_is_captcha_hidden_cu.json`;
    protected forgotPassUrl = `${this.restUrlsConf.appURL}/public/auth/forgot_pass/sendEmail.json`;
    protected changePassUrl = `${this.restUrlsConf.appURL}/public/auth/change_pass.json`;
    protected userHasSecretCodeUrl = `${this.restUrlsConf.appURL}/public/auth/is_user_has_secret_code.json`;
    protected sendLoginSmsUrl = `${this.restUrlsConf.appURL}/public/auth/login_sms_code`;
    protected sendBusinessLoginSmsUrl = `${this.restUrlsConf.appURL}/public/auth/business/login_sms_code`;
    protected verifyLoginSmsUrl = `${this.restUrlsConf.appURL}/public/auth/verify_login_sms_code`;
    protected verifyBusinessLoginSmsUrl = `${this.restUrlsConf.appURL}/public/auth/business/verify_login_sms_code`;
    private polUrl = `${this.restUrlsConf.appURL}/phone-order/enable`;
    private poloutUrl = `${this.restUrlsConf.appURL}/auth/phone-order/logout`;
    protected allowAdminChangePassUrl = `${this.restUrlsConf.appURL}/auth/allow_admin_change_path`;
    protected isPermissionToGroup = `${this.restUrlsConf.appURL}/public/auth/check_permission_to_group`;

    public usersData$ = new BehaviorSubject<IUser>(null);
    public currentUsersData$ = new BehaviorSubject<IUser>(null);
    public isLoggedIn = false;

    constructor(protected userService: UserService,
                protected shareDataService: ShareDataService,
                protected restUrlsConf: RestUrlsConfig,
                protected http: HttpClient,
                protected notifySvc: PageNotificationService,
                protected router: Router,
                protected phoneSharedSvc: PhoneOrderSharedService
    ) { }

    public handleAuthentication(redirectUrl = ''): void {
        // gets user details
        if (GenericConfig.appNonUsers) {
            this.useUserDetails(UserFactory.create({}), redirectUrl);
        } else {
            this.userService.getUserDetails().subscribe(
                (user: IUser) => {
                    this.useUserDetails(user, redirectUrl);
                },
                error => {
                    this.notifySvc.set(error).show();
                }
            );
        }
    }

    private useUserDetails(user: IUser, redirectUrl) {
        const isPhoneOrder = _.has(user, 'phone_order_mode') && (user['phone_order_mode'] === true);
        if (this.phoneSharedSvc.phoneOrderMode !== isPhoneOrder) {
            this.phoneSharedSvc.setPhoneOrderState(isPhoneOrder);
        }
        const isLogin = Boolean(user instanceof SignedUserDTO);
        this.setIsLoggedIn(isLogin);
        this.setUncommerce(isLogin);
        /*Initialize isLogin*/
        this.shareDataService.isLogin(isLogin);
        let restrictedLogin = GenericConfig.restrictedLogin || false;
        this.shareDataService.isRestricted(restrictedLogin && !isLogin);
        this.usersData$.next(user);
        if (!isPhoneOrder) {
            this.currentUsersData$.next(user);
        }
        if (redirectUrl) {
            if (/^https?:\/\//i.test(redirectUrl)) {
                window.location.href = redirectUrl;
            } else {
                this.router.navigate([redirectUrl]);
            }
        }
    }

    private setUncommerce(isLogin) {
        if(GenericConfig.commerceApp == 'logout') {
            GenericConfig.unCommerceApp = isLogin ? false : true;
        }
    }

    public setIsLoggedIn(isLoggedIn) {
        this.isLoggedIn = isLoggedIn;
    }

    isRouteAuthenticated(url: any): Observable<any> {
        return this.http.post(this.routeAuthUrl, url)
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    checkIsCaptchaHidden(form = 'login'): Observable<any> {
        return this.http.get(this.isCaptchaHidden + form)
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    forgotPassSendEmail(form: any): Observable<any> {
        return this.http.post(this.forgotPassUrl, JSON.stringify(form))
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    changePassword(form: any): Observable<any> {
        return this.http.post(this.changePassUrl, JSON.stringify(form))
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    userHasSecretCode(): Observable<any> {
        return this.http.get(this.userHasSecretCodeUrl)
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    sendSmsLoginCode(form: any): Observable<any> {
        return this.http.post(this.sendLoginSmsUrl, JSON.stringify(form))
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    // FIXME: temporary here. should be moved to app-ratag
    sendSmsBusinessLoginCode(form: any): Observable<any> {
        return this.http.post(this.sendBusinessLoginSmsUrl, JSON.stringify(form))
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    verifySmsLoginCode(form: any): Observable<any> {
        return this.http.post(this.verifyLoginSmsUrl, JSON.stringify(form))
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    // FIXME: temporary here. should be moved to app-ratag
    verifySmsBusinessLoginCode(form: any): Observable<any> {
        return this.http.post(this.verifyBusinessLoginSmsUrl, JSON.stringify(form))
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    phoneOrderLogout(): Observable<any> {
        return this.http.get(this.poloutUrl)
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }

    allowAdminChangePass(): Observable<any> {
        return this.http.get<any>(this.allowAdminChangePassUrl)
            .pipe(tap((res: HttpResponse<any>) => res, (error: any) => throwError(error)));
    }
    checkPermissionToGroup($namePermission: string){
        return this.http.get(`${this.isPermissionToGroup}/${$namePermission}`)
            .pipe(tap((res) => res, (error) => throwError(error)));
    }
}
